import { DatePickerDate } from '@smartsheet/lodestar-core';
import moment from 'moment';
import 'moment-precise-range-plugin';

/**
 * Convert a timestamp of a greater granularity than seconds to seconds.
 * @param time - a timestamp of greater granularity than seconds.
 */
export const toSeconds = (time: string | number) =>
  Number.parseInt((time + '').substring(0, 10), 10);

/**
 * Convert a timestamp of a lesser granularity than nanoseconds to nanoseconds.
 * @param time - a timestamp of lesser granularity than nanoseconds.
 */
export const toNanoseconds = (time: string | number) =>
  (time + '').padEnd(19, '0');

export const toUnixTimestamp = (date: DatePickerDate) =>
  new Date(date.year, date.month, date.day).getTime().toString();

export const toDatePickerDate = (timestamp: string): DatePickerDate | null => {
  if (!timestamp.length) {
    return null;
  }
  const date = new Date(parseInt(timestamp));

  return {
    day: date.getDate(),
    month: date.getMonth(),
    year: date.getFullYear(),
  };
};

const getUnit = (unit: string, value: number) => {
  if (value > 1) {
    return unit;
  }

  return unit.slice(0, unit.length - 1);
};

interface TimeSegment {
  value: number;
  unit: string;
}

const buildTimeString = (timeSegments: TimeSegment[]): string => {
  const [segment, ...more] = timeSegments;
  const timeString = `${segment.value} ${getUnit(segment.unit, segment.value)}`;
  if (more.length) {
    return `${timeString} ${buildTimeString(more)}`;
  } else {
    return timeString;
  }
};
export const timeDifference = (startTime: number, endTime?: number) => {
  // parse input times
  const start = moment.unix(toSeconds(startTime));
  const end = endTime ? moment.unix(toSeconds(endTime)) : moment();

  // caculate time difference in segments
  const { years, months, days, hours, minutes, seconds } = moment.preciseDiff(
    start,
    end,
    true
  );

  // add unit for each segment
  let timeSegments: TimeSegment[] = [];
  timeSegments.push({ value: years, unit: 'years' });
  timeSegments.push({ value: months, unit: 'months' });
  timeSegments.push({ value: days, unit: 'days' });
  timeSegments.push({ value: hours, unit: 'hours' });
  timeSegments.push({ value: minutes, unit: 'minutes' });
  timeSegments.push({ value: seconds, unit: 'seconds' });

  // largest two consecutive segments only
  if (years) timeSegments = timeSegments.slice(0, 2);
  else if (months) timeSegments = timeSegments.slice(1, 3);
  else if (days) timeSegments = timeSegments.slice(2, 4);
  else if (hours) timeSegments = timeSegments.slice(3, 5);
  else timeSegments = timeSegments.slice(4, 6);

  // filter out zero values
  timeSegments = timeSegments.filter(segment => segment.value > 0);

  // build the return string
  let timeString;
  if (timeSegments.length) timeString = buildTimeString(timeSegments);
  else timeString = 'Less than a second';

  return timeString;
};
